import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { sanitizeDiscoveryParams } from "@/components/shell/galleryDiscoveryFacets";
import { DiscoveryPanelsProvider } from "@/components/shell/DiscoveryPanels";

const SEARCH_DEBOUNCE_MS = 250;
const BROWSE_MODE_KEY = "ato.browseMode";

export type BrowseMode = "recommended" | "latest" | "trending" | "liked";
export type SearchSort = "popular" | "latest";

export type SearchContextValue = {
  open: boolean;
  openPalette: () => void;
  closePalette: () => void;
  query: string;
  setQuery: (q: string) => void;
  debouncedQuery: string;
  searching: boolean;
  browseMode: BrowseMode;
  setBrowseMode: (m: BrowseMode) => void;
  searchSort: SearchSort;
  setSearchSort: (s: SearchSort) => void;
  surface: string;
  setSurface: (s: string) => void;
  /** Active gallery category filter (empty = Recommended / all). */
  category: string;
  /** Active gallery subcategory filter (paired with `category`). */
  subcategory: string;
  /** Set (or replace) the active category filter. */
  setCategory: (category: string, subcategory?: string) => void;
  /** Clear the active category filter (back to Recommended / all apps). */
  clearCategory: () => void;
};

const SearchContext = createContext<SearchContextValue | null>(null);

function isTypingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

function initialParam(name: string): string {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get(name) ?? "";
}

function initialBrowseMode(): BrowseMode {
  if (typeof window === "undefined") return "recommended";
  try {
    // The `feed` URL param is authoritative (shareable/deep-linkable); fall
    // back to the persisted session choice, then to Recommended.
    const feed = initialParam("feed");
    if (feed === "latest" || feed === "trending" || feed === "liked")
      return feed;
    const stored = sessionStorage.getItem(BROWSE_MODE_KEY);
    if (
      stored === "recommended" ||
      stored === "latest" ||
      stored === "trending" ||
      stored === "liked"
    ) {
      return stored;
    }
  } catch {}
  return "recommended";
}

export default function SearchProvider({
  children,
  navigationQuery,
  onNavigationQuery,
}: {
  children: ReactNode;
  navigationQuery?: string;
  onNavigationQuery?: (query: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [localQuery, setLocalQuery] = useState(
    () => navigationQuery ?? initialParam("q"),
  );
  const query = localQuery;
  useEffect(() => {
    if (navigationQuery !== undefined)
      setLocalQuery((current) =>
        current.trim() === navigationQuery ? current : navigationQuery,
      );
  }, [navigationQuery]);
  const setQuery = useCallback(
    (q: string) => {
      setLocalQuery(q);
      onNavigationQuery?.(q);
    },
    [onNavigationQuery],
  );
  const [debouncedQuery, setDebouncedQuery] = useState(() =>
    initialParam("q").trim(),
  );
  const [surface, setSurface] = useState(() => initialParam("surface"));
  const debounceRef = useRef<number | null>(null);
  const [browseMode, _setBrowseMode] = useState<BrowseMode>(() =>
    initialBrowseMode(),
  );
  const [searchSort, _setSearchSort] = useState<SearchSort>(() => {
    const stored = initialParam("sort");
    if (stored === "latest") return "latest";
    return "popular";
  });
  const [filter, setFilter] = useState(() => {
    const { category: cat, subcategory } = sanitizeDiscoveryParams(
      initialParam("category"),
      initialParam("subcategory"),
    );
    return { category: cat, subcategory };
  });
  const category = filter.category;
  const subcategory = filter.subcategory;

  const setBrowseMode = useCallback((m: BrowseMode) => {
    _setBrowseMode(m);
    try {
      sessionStorage.setItem(BROWSE_MODE_KEY, m);
    } catch {}
  }, []);

  const setSearchSort = useCallback((s: SearchSort) => {
    _setSearchSort(s);
  }, []);

  const setCategory = useCallback(
    (nextCategory: string, nextSubcategory?: string) => {
      const { category: cat, subcategory } = sanitizeDiscoveryParams(
        nextCategory,
        nextSubcategory ?? "",
      );
      setFilter({ category: cat, subcategory });
    },
    [],
  );

  const clearCategory = useCallback(() => {
    setFilter({ category: "", subcategory: "" });
  }, []);

  useEffect(() => {
    debounceRef.current = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current !== null)
        window.clearTimeout(debounceRef.current);
    };
  }, [query]);

  // Starting a global search drops the category filter: browsing a category
  // and typing a query are exclusive surfaces (the grid becomes "Search
  // results for …" with the server's own ranking, not a category feed).
  useEffect(() => {
    if (debouncedQuery.length > 0 && category) {
      clearCategory();
    }
  }, [debouncedQuery, category, clearCategory]);

  // Keep the `feed` URL param in sync with browseMode (replaceState: same
  // history entry, so browser-back doesn't bounce around feed changes).
  useEffect(() => {
    if (navigationQuery !== undefined) return;
    const url = new URL(window.location.href);
    const current = url.searchParams.get("feed") ?? "";
    const feed = browseMode === "recommended" ? "" : browseMode;
    if (current === feed || (!current && !feed)) return;
    if (feed) url.searchParams.set("feed", feed);
    else url.searchParams.delete("feed");
    history.replaceState(null, "", url);
  }, [browseMode, navigationQuery]);

  // Category/subcategory are real navigation state → pushState (browser
  // back/forward restores the filter, see the popstate handler below).
  useEffect(() => {
    if (navigationQuery !== undefined) return;
    const url = new URL(window.location.href);
    const currentCategory = url.searchParams.get("category") ?? "";
    const currentSubcategory = url.searchParams.get("subcategory") ?? "";
    if (currentCategory === category && currentSubcategory === subcategory) {
      return;
    }
    if (category) url.searchParams.set("category", category);
    else url.searchParams.delete("category");
    if (subcategory) url.searchParams.set("subcategory", subcategory);
    else url.searchParams.delete("subcategory");
    history.pushState(null, "", url);
  }, [category, subcategory, navigationQuery]);

  // Browser back/forward: restore category + subcategory + feed from the URL.
  useEffect(() => {
    if (navigationQuery !== undefined) return;
    const onPopState = () => {
      const params = new URLSearchParams(window.location.search);
      const sanitized = sanitizeDiscoveryParams(
        params.get("category") ?? "",
        params.get("subcategory") ?? "",
      );
      setFilter((prev) =>
        prev.category === sanitized.category &&
        prev.subcategory === sanitized.subcategory
          ? prev
          : sanitized,
      );
      const feed = params.get("feed");
      if (
        feed === "latest" ||
        feed === "trending" ||
        feed === "liked" ||
        feed === "recommended"
      ) {
        _setBrowseMode(feed);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const openPalette = useCallback(() => {
    setOpen(true);
    if (navigationQuery !== undefined && window.innerWidth >= 900)
      (document.getElementById("header-app-search") ?? document.getElementById("discovery-search"))?.focus();
  }, [navigationQuery]);
  const closePalette = useCallback(() => setOpen(false), []);

  // Discover-only palette shortcut. ⌘K / Ctrl+K belongs to the header's app
  // search (or, without the Discover workspace, the My Apps launcher).
  // "/" opens the inline Discover filter using its own navigation state.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "/" &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !isTypingTarget(e.target)
      ) {
        e.preventDefault();
        openPalette();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [openPalette]);

  const searching = debouncedQuery.length > 0;

  const value = useMemo<SearchContextValue>(
    () => ({
      open,
      openPalette,
      closePalette,
      query,
      setQuery,
      debouncedQuery,
      searching,
      browseMode,
      setBrowseMode,
      searchSort,
      setSearchSort,
      surface,
      setSurface,
      category,
      subcategory,
      setCategory,
      clearCategory,
    }),
    [
      open,
      openPalette,
      closePalette,
      query,
      setQuery,
      debouncedQuery,
      searching,
      browseMode,
      searchSort,
      surface,
      category,
      subcategory,
      setCategory,
      clearCategory,
    ],
  );

  return (
    <SearchContext.Provider value={value}>
      <DiscoveryPanelsProvider>{children}</DiscoveryPanelsProvider>
    </SearchContext.Provider>
  );
}

/** The search context when rendered inside SearchProvider, else `null`. */
export function useOptionalSearch(): SearchContextValue | null {
  return useContext(SearchContext);
}

export function useSearch(): SearchContextValue {
  const ctx = useContext(SearchContext);
  if (!ctx) throw new Error("useSearch must be used within SearchProvider");
  return ctx;
}
